<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CrmCard from './CrmCard.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  stage: {
    type: Object,
    required: true,
  },
  conversations: {
    type: Array,
    default: () => [],
  },
  columnTotal: {
    type: Number,
    default: 0,
  },
  formattedColumnTotal: {
    type: String,
    default: 'R$ 0,00',
  },
  getSlaInfo: {
    type: Function,
    required: true,
  },
  getChannelInfo: {
    type: Function,
    required: true,
  },
  getSourceInfo: {
    type: Function,
    required: true,
  },
  getTeamInfo: {
    type: Function,
    required: true,
  },
  getDealValue: {
    type: Function,
    required: true,
  },
  getDealStatus: {
    type: Function,
    required: true,
  },
  formatCurrency: {
    type: Function,
    required: true,
  },
  formatDateTime: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits([
  'drop-card',
  'assign-me',
  'resolve',
  'update-deal-value',
  'update-deal-status',
  'rename-lead',
  'delete-card',
  'duplicate-card',
  'edit-card',
]);

const { t } = useI18n();
const isDragOver = ref(false);

function onDragOver(event) {
  event.preventDefault();
  isDragOver.value = true;
}

function onDragLeave() {
  isDragOver.value = false;
}

function onDrop(event) {
  event.preventDefault();
  isDragOver.value = false;
  const conversationIdStr = event.dataTransfer.getData('text/plain');
  if (conversationIdStr) {
    const conversationId = Number(conversationIdStr);
    emit('drop-card', conversationId, props.stage.id);
  }
}
</script>

<template>
  <div
    class="flex h-full w-[320px] flex-shrink-0 flex-col rounded-2xl border border-n-weak bg-n-solid-1/60 shadow-sm transition-colors duration-150 overflow-hidden"
    :class="{
      'border-n-brand bg-n-brand/5 ring-2 ring-n-brand/20': isDragOver,
    }"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <!-- Column Header: Stage Name, Count & Total Value Sum (R$) -->
    <div
      class="flex flex-col gap-1 border-b border-n-weak px-4 py-3 bg-n-surface-1/60"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2 min-w-0">
          <span
            class="size-3 rounded-full flex-shrink-0"
            :style="{ backgroundColor: stage.color || '#3B82F6' }"
          />
          <h3
            class="text-xs font-extrabold text-n-slate-12 truncate max-w-[150px]"
          >
            {{ stage.name }}
          </h3>
          <span
            class="flex size-5 items-center justify-center rounded-full bg-n-solid-2 text-[11px] font-extrabold text-n-slate-11 border border-n-weak"
          >
            {{ conversations.length }}
          </span>
        </div>

        <!-- Total Value Sum in Column (R$) -->
        <span
          class="inline-flex items-center rounded-md bg-emerald-500/10 px-2 py-0.5 text-xs font-extrabold text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
          :title="t('CRM.COLUMN.SUM_TOOLTIP')"
        >
          {{ formattedColumnTotal }}
        </span>
      </div>
    </div>

    <!-- Cards Scroll Area -->
    <div
      class="flex flex-1 flex-col gap-2.5 overflow-y-auto p-3 custom-scrollbar"
    >
      <CrmCard
        v-for="conversation in conversations"
        :key="conversation.id"
        :conversation="conversation"
        :sla-info="getSlaInfo(conversation)"
        :channel-info="getChannelInfo(conversation)"
        :source-info="getSourceInfo(conversation)"
        :team-name="getTeamInfo(conversation)"
        :deal-value="getDealValue(conversation)"
        :deal-status="getDealStatus(conversation)"
        :formatted-currency="formatCurrency(getDealValue(conversation))"
        :formatted-date-time="formatDateTime(conversation.created_at || conversation.waiting_since)"
        @assign-me="cid => emit('assign-me', cid)"
        @resolve="cid => emit('resolve', cid)"
        @update-deal-value="(cid, val) => emit('update-deal-value', cid, val)"
        @update-deal-status="(cid, st) => emit('update-deal-status', cid, st)"
        @rename-lead="(cid, name) => emit('rename-lead', cid, name)"
        @delete-card="cid => emit('delete-card', cid)"
        @duplicate-card="conv => emit('duplicate-card', conv)"
        @edit-card="conv => emit('edit-card', conv)"
      />

      <div
        v-if="conversations.length === 0"
        class="flex flex-1 flex-col items-center justify-center p-6 text-center text-xs text-n-slate-10 border-2 border-dashed border-n-weak/50 rounded-xl m-1"
      >
        <Icon icon="i-lucide-inbox" class="size-6 mb-1 opacity-40" />
        <span>{{ t('CRM.EMPTY_COLUMN') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(150, 150, 150, 0.2);
  border-radius: 3px;
}
</style>
